/**
 * Delete-account sheet (V1 auth-profiles-gdpr) — the SM-12 REQUEST_DELETE
 * surface. Dead serious, zero humor (copy-tone §1.4).
 *
 * Two proofs, both server-verified: a fresh reauth (POST /auth/reauth →
 * ≤5 min token) and the typed DELETE confirmation. From ACTIVE the server
 * enters the 14-day grace and revokes EVERY session including this one; from
 * pre-ACTIVE lifecycles (registered_unverified / onboarding) deletion is
 * immediate — no grace (S-05 footer rule). Used from S-25 and from the
 * S-05…S-09 "Delete my data" footers.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { copy } from "@humble/shared";
import { Button, Sheet, toast } from "../../design/index.js";
import { api, isApiError } from "../../lib/api.js";
import { clearSession, sessionStore, useStore } from "../../lib/store.js";

export interface DeleteAccountSheetProps {
  open: boolean;
  onClose: () => void;
  /** Pre-ACTIVE lifecycles delete immediately — the copy states it plainly. */
  lifecycle?: string | null;
}

export function DeleteAccountSheet({ open, onClose, lifecycle }: DeleteAccountSheetProps) {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const preActive = lifecycle === "registered_unverified" || lifecycle === "onboarding";

  async function onDelete() {
    if (busy) return;
    if (confirmation !== "DELETE") {
      setError('Type DELETE (exactly) to confirm.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const reauth = await api.post("/auth/reauth", { body: { password } });
      if (!reauth?.reauth_token) throw new Error("reauth failed");
      await api.post("/me/delete", {
        body: { reauth_token: reauth.reauth_token, confirmation: "DELETE" },
      });
      clearSession();
      toast(
        preActive
          ? "Your account and data have been deleted."
          : "Deletion scheduled. Logging in during the next 14 days lets you cancel.",
        "success",
      );
      navigate("/");
    } catch (err) {
      if (isApiError(err) && err.status === 401) {
        setError("That password is wrong. Deletion needs fresh proof it's you.");
      } else if (isApiError(err)) {
        setError(err.message);
      } else {
        setError("Network error — nothing was deleted. Try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet open={open} onClose={onClose} title="Delete account">
      <p>
        {preActive
          ? "Your account is not active yet, so deletion is immediate and permanent: no 14-day grace period, no way back. Your email, date of birth and anything you uploaded are erased."
          : copy.safety.delete_confirm}
      </p>
      <div className="field">
        <label htmlFor="delete-password">Your password</label>
        <input
          id="delete-password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      <p className="text-faint" style={{ fontSize: "0.85rem" }}>
        No password on this account (Google or email-link only)? Add one first in Settings, or
        contact support — deletion always requires fresh proof of identity.
      </p>
      <div className="field">
        <label htmlFor="delete-confirmation">Type DELETE to confirm</label>
        <input
          id="delete-confirmation"
          type="text"
          autoComplete="off"
          value={confirmation}
          onChange={(e) => setConfirmation(e.target.value)}
        />
      </div>
      {error && (
        <p className="field__error" role="alert">
          {error}
        </p>
      )}
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <Button
          block
          variant="danger"
          loading={busy}
          disabled={confirmation !== "DELETE" || password.length === 0}
          onClick={() => void onDelete()}
        >
          Delete my account
        </Button>
        <Button block variant="ghost" onClick={onClose}>
          Keep my account
        </Button>
      </div>
    </Sheet>
  );
}

/**
 * "Delete my data" footer link (S-05 and every onboarding screen, S-06–S-09).
 * With a session it opens the sheet; without one it explains the automatic
 * purge honestly (unverified accounts self-delete after 7 days).
 */
export function DeleteDataFooter() {
  const status = useStore(sessionStore, (s) => s.status);
  const lifecycle = useStore(sessionStore, (s) => s.lifecycle);
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(false);

  return (
    <>
      <button
        type="button"
        className="btn btn--sm btn--ghost"
        onClick={() => (status === "authed" ? setOpen(true) : setNote(true))}
      >
        Delete my data
      </button>
      {note && (
        <p className="text-faint" role="note" style={{ fontSize: "0.85rem" }}>
          Nothing to delete yet from here: unverified accounts are erased automatically after 7
          days, and no profile exists until onboarding completes. To delete immediately, sign in
          first (the link in your inbox works), then use this same link.
        </p>
      )}
      <DeleteAccountSheet open={open} onClose={() => setOpen(false)} lifecycle={lifecycle} />
    </>
  );
}
